import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// Szerepkörök és jogosultságok
const ROLES = {
    ADMIN: 'admin',
    MANAGER: 'manager',
    WORKER: 'worker'
};

const PERMISSIONS = {
    [ROLES.ADMIN]: [
        'manage_users',
        'manage_projects',
        'manage_references',
        'manage_calendar',
        'manage_payments',
        'upload_images',
        'view_all_data'
    ],
    [ROLES.MANAGER]: [
        'manage_projects',
        'manage_calendar',
        'manage_payments',
        'upload_images',
        'view_worker_data'
    ],
    [ROLES.WORKER]: [
        'view_own_projects',
        'view_own_calendar',
        'view_own_payments',
        'upload_images',
        'update_own_profile'
    ]
};
export const API_URL = process.env.REACT_APP_API_URL;

// API Kliens
const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Token interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            verifyToken(token);
        } else {
            setLoading(false);
        }
    }, []);

    const verifyToken = async (token) => {
        try {
            const response = await api.get('/auth/verify');
            if (response.data.success) {
                setIsAuthenticated(true);
                setCurrentUser(response.data.user);
                return true;
            } else {
                throw new Error('Token verification failed');
            }
        } catch (err) {
            console.error('Token verification failed:', err);
            logout();
            return false;
        } finally {
            setLoading(false);
        }
    };

    const login = async (username, password) => {
        try {
            const response = await api.post('/auth/login', { username, password });
            if (response.data.success && response.data.token) {
                localStorage.setItem('token', response.data.token);
                setIsAuthenticated(true);
                setCurrentUser(response.data.user);
                return true;
            }
            return false;
        } catch (err) {
            console.error('Login failed:', err);
            return false;
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('currentUser');
        setIsAuthenticated(false);
        setCurrentUser(null);
    };

    const addUser = async (userData) => {
        try {
            const response = await api.post('/users', userData);
            return response.data.success ? response.data.userId : false;
        } catch (err) {
            console.error('Add user failed:', err);
            return false;
        }
    };

    const updateUser = async (userId, updatedData) => {
        try {
            const response = await api.put(`/users/${userId}`, updatedData);
            if (response.data.success) {
                if (currentUser?.id === userId) {
                    const userResponse = await api.get(`/users/${userId}`);
                    if (userResponse.data.success) {
                        setCurrentUser(userResponse.data.user);
                    }
                }
                return true;
            }
            return false;
        } catch (err) {
            console.error('Update user failed:', err);
            return false;
        }
    };

    const deleteUser = async (userId) => {
        try {
            const response = await api.delete(`/users/${userId}`);
            return response.data.success;
        } catch (err) {
            console.error('Delete user failed:', err);
            return false;
        }
    };

    const hasPermission = (permission) => {
        if (!currentUser) return false;
        return PERMISSIONS[currentUser.role].includes(permission);
    };

    const value = {
        isAuthenticated,
        currentUser,
        loading,
        login,
        logout,
        addUser,
        updateUser,
        deleteUser,
        hasPermission,
        ROLES,
        PERMISSIONS
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext; 